import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Building2, CalendarCheck, FileText, ClipboardList, LineChart, Layers, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { pmoc, site, waLink, waMessages } from "@/content/siteContent";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const cardIcons = [CalendarCheck, ClipboardList, Layers, FileText, LineChart, Building2];

const initialForm = {
  nome: "",
  empresa: "",
  whatsapp: "",
  email: "",
  cidade: "Cuiabá",
  tipo: "",
  quantidade: "",
  equipamento: "",
  mensagem: "",
};

export default function Pmoc() {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(initialForm);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const message = [
      waMessages.pmoc,
      "",
      `Nome: ${form.nome}`,
      form.empresa && `Empresa: ${form.empresa}`,
      `WhatsApp: ${form.whatsapp}`,
      form.email && `E-mail: ${form.email}`,
      form.cidade && `Cidade: ${form.cidade}`,
      form.tipo && `Estabelecimento: ${form.tipo}`,
      form.quantidade && `Aparelhos: ${form.quantidade}`,
      form.equipamento && `Equipamentos: ${form.equipamento}`,
      form.mensagem && `Mensagem: ${form.mensagem}`,
    ].filter(Boolean).join("\n");

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        nome: form.nome,
        empresa: form.empresa,
        whatsapp: form.whatsapp,
        email: form.email,
        cidade: form.cidade,
        tipo_estabelecimento: form.tipo,
        quantidade_aparelhos: form.quantidade,
        equipamentos: form.equipamento,
        mensagem: form.mensagem,
        origem: "pmoc",
      });
    } catch (err) {
      console.error("Falha ao registrar lead", err);
    }

    window.open(waLink(message), "_blank", "noopener,noreferrer");
    toast.success("Solicitação pronta! Abrimos o WhatsApp com os seus dados.");
    setSending(false);
    setOpen(false);
    setForm(initialForm);
  };

  return (
    <section id="pmoc" className="border-y border-metal bg-graphite py-24 md:py-32" data-testid="pmoc-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          chapter={pmoc.chapter}
          microtitle={pmoc.microtitle}
          titleA={pmoc.titleA}
          titleB={pmoc.titleB}
          highlightWord={pmoc.highlightWord}
          description={pmoc.subtitle}
        />

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {pmoc.cards.map((card, i) => {
            const Icon = cardIcons[i];
            return (
              <Reveal key={card.title} delay={(i % 3) * 0.1}>
                <article className="card-industrial h-full p-7" data-testid={`pmoc-card-${i + 1}`}>
                  <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl border border-metal bg-ink">
                    <Icon className="h-5 w-5 text-brand" strokeWidth={1.8} />
                  </span>
                  <h3 className="font-display text-[0.8rem] font-extrabold tracking-[0.14em] text-paper">{card.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-mist">{card.desc}</p>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="pmoc-publics">
            <span className="microtitle mr-2">PARA QUEM:</span>
            {pmoc.publics.map((p) => (
              <span key={p} className="rounded-full border border-metal bg-ink px-4 py-2 text-xs font-bold tracking-wide text-mist">
                {p}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.22}>
          <div className="mt-12 flex flex-col items-start gap-6 md:flex-row md:items-center">
            <button onClick={() => setOpen(true)} className="btn-industrial" data-testid="pmoc-open-form-button">
              {pmoc.cta}
              <ArrowRight className="h-4 w-4" />
            </button>
            <p className="max-w-md text-xs leading-relaxed text-steel">{pmoc.note}</p>
          </div>
        </Reveal>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="honeycomb max-h-[92vh] overflow-y-auto rounded-[20px] border-metal bg-graphite p-0 text-paper sm:max-w-xl"
          data-testid="pmoc-modal"
        >
          <div className="stripes h-2 w-full" />
          <div className="relative z-10 p-7 md:p-9">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-paper">
                ORÇAMENTO DE <span className="text-brand">PMOC</span>
              </DialogTitle>
              <DialogDescription className="text-sm text-mist">
                Preencha os dados e envie direto para o nosso WhatsApp.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-7 grid gap-4" data-testid="pmoc-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input required value={form.nome} onChange={set("nome")} placeholder="Nome*" className="field-industrial" data-testid="pmoc-input-nome" />
                <input value={form.empresa} onChange={set("empresa")} placeholder="Empresa" className="field-industrial" data-testid="pmoc-input-empresa" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp*" className="field-industrial" data-testid="pmoc-input-whatsapp" />
                <input type="email" value={form.email} onChange={set("email")} placeholder="E-mail" className="field-industrial" data-testid="pmoc-input-email" />
              </div>
              <input value={form.cidade} onChange={set("cidade")} placeholder="Cidade" className="field-industrial" data-testid="pmoc-input-cidade" />
              <div className="grid gap-4 sm:grid-cols-3">
                <select required value={form.tipo} onChange={set("tipo")} className="field-industrial" data-testid="pmoc-select-tipo">
                  <option value="" disabled>Estabelecimento*</option>
                  {pmoc.form.establishmentTypes.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <select required value={form.quantidade} onChange={set("quantidade")} className="field-industrial" data-testid="pmoc-select-quantidade">
                  <option value="" disabled>Aparelhos*</option>
                  {pmoc.form.unitCounts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <select required value={form.equipamento} onChange={set("equipamento")} className="field-industrial" data-testid="pmoc-select-equipamento">
                  <option value="" disabled>Equipamentos*</option>
                  {pmoc.form.equipmentTypes.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <textarea
                value={form.mensagem}
                onChange={set("mensagem")}
                placeholder="Mensagem (opcional)"
                rows={3}
                className="field-industrial resize-none"
                data-testid="pmoc-input-mensagem"
              />
              <button type="submit" disabled={sending} className="btn-industrial w-full disabled:opacity-60" data-testid="pmoc-submit-button">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {sending ? "ENVIANDO..." : "SOLICITAR AVALIAÇÃO"}
              </button>
              <p className="text-center text-[0.68rem] tracking-wide text-steel">
                Envio direto para {site.whatsappDisplay} via WhatsApp
              </p>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
